import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, Smartphone, Copy, CheckCircle2, AlertTriangle, Clock, RefreshCw, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { api, auth } from "@/lib/api";
import { RecoveryCodesPanel } from "@/components/RecoveryCodesPanel";

/**
 * TwoFASetup renders three distinct views based on the logged-in user's state:
 *  - "enroll"     → first-time enrollment: QR + manual key + code verify form
 *  - "show-codes" → display the 10 backup recovery codes after successful enable or regenerate
 *  - "manage"     → already-enrolled users see status (X/10 remaining) + Regenerate button
 *
 * The state machine reads /auth/me on mount to pick the right starting view.
 */
export default function TwoFASetup() {
  const navigate = useNavigate();
  const [view, setView] = useState("loading"); // loading | enroll | show-codes | manage
  const [userEmail, setUserEmail] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);

  // Enrollment state
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [skipping, setSkipping] = useState(false);
  const [confirmSkip, setConfirmSkip] = useState(false);
  const [clockDrift, setClockDrift] = useState(null);

  // Recovery codes state
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [recoveryStatus, setRecoveryStatus] = useState(null); // {total, remaining, generated_at}
  const [regenerating, setRegenerating] = useState(false);
  const [confirmRegen, setConfirmRegen] = useState(false);
  const [regeneratingQr, setRegeneratingQr] = useState(false);

  const regenerateQr = async () => {
    if (!window.confirm(
      "Generate a NEW QR code? Your current authenticator entry will stop working — you'll need to remove it and scan the new QR."
    )) return;
    setRegeneratingQr(true);
    try {
      const s = await api.post("/auth/2fa/setup", { regenerate: true });
      setSetup(s.data);
      setCode("");
      toast.success("New QR generated — re-scan it into your authenticator.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not regenerate QR");
    } finally { setRegeneratingQr(false); }
  };

  // Decide which view to render based on the user's current enrollment status.
  useEffect(() => {
    (async () => {
      try {
        // SEC-004: users arriving here from the login `must_setup_2fa` branch
        // carry a scope=2fa_setup token that CANNOT hit `/auth/me` (or any
        // protected route). Detect that state via the local user object's
        // `twofa_pending_setup` flag set by Login.jsx and skip the /me probe.
        const stored = auth.user() || {};
        const isPendingSetup = stored.twofa_pending_setup === true;
        if (isPendingSetup) {
          setUserEmail(stored.email || "");
          setIsAdmin(false); // role unknown until full session; safe default
          const s = await api.post("/auth/2fa/setup");
          setSetup(s.data);
          api.get("/auth/2fa/clock").then(r => {
            setClockDrift(Math.floor(Date.now() / 1000) - r.data.server_epoch);
          }).catch(() => {});
          setView("enroll");
          return;
        }
        // Full session — safe to hit /auth/me
        const me = await api.get("/auth/me");
        setUserEmail(me.data.email);
        setIsAdmin(me.data.role === "admin");
        if (me.data.twofa_enabled) {
          const status = await api.get("/auth/2fa/recovery-codes/status");
          setRecoveryStatus(status.data);
          setView("manage");
        } else {
          const s = await api.post("/auth/2fa/setup");
          setSetup(s.data);
          api.get("/auth/2fa/clock").then(r => {
            setClockDrift(Math.floor(Date.now() / 1000) - r.data.server_epoch);
          }).catch(() => {});
          setView("enroll");
        }
      } catch {
        toast.error("Could not initialize 2FA setup");
        navigate("/login");
      }
    })();
  }, [navigate]);

  const copy = () => {
    if (setup?.secret) {
      navigator.clipboard.writeText(setup.secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/auth/2fa/enable", { code });
      toast.success("2FA enabled — save your recovery codes");
      setRecoveryCodes(r.data.recovery_codes || []);
      // SEC-004 hardening: backend returns a fresh full-scope JWT so the user
      // can continue without another login round-trip. Persist it + upgrade
      // the local user object (clear twofa_pending_setup flag).
      if (r.data.token) {
        localStorage.setItem("medlogix_token", r.data.token);
        const cur = auth.user() || {};
        localStorage.setItem("medlogix_user", JSON.stringify({
          ...cur, twofa_enabled: true, twofa_pending_setup: false,
        }));
      }
      setView("show-codes");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid code");
    } finally { setLoading(false); }
  };

  const skip = async () => {
    // 2FA skip was deprecated in SEC-001 hardening — TOTP is now mandatory for
    // every account. Bail out here so users don't get a confusing 410 toast.
    toast.error("2FA is now mandatory for all accounts and cannot be skipped.");
  };

  const regenerate = async () => {
    setRegenerating(true);
    try {
      const r = await api.post("/auth/2fa/recovery-codes/regenerate");
      setRecoveryCodes(r.data.recovery_codes || []);
      toast.success("New recovery codes generated — old ones are now invalid");
      setView("show-codes");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not regenerate codes");
    } finally { setRegenerating(false); setConfirmRegen(false); }
  };

  const finishEnrollment = () => {
    // After codes are saved, log out so the user signs in fresh with the new 2FA gate.
    auth.logout();
    navigate("/login");
  };

  const finishRegen = () => {
    // After regen, user is still logged in — return them to the dashboard.
    navigate("/dashboard");
  };

  return (
    <div className="min-h-screen bg-brand-bg flex items-center justify-center p-6" data-testid="2fa-setup-page">
      <div className="max-w-xl w-full bg-white border border-brand rounded-2xl p-5 sm:p-8 shadow-sm">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl bg-brand-primary flex items-center justify-center">
            <ShieldCheck className="w-6 h-6 text-white" />
          </div>
          <div>
            <div className="eyebrow">
              {view === "manage" ? "Two-Factor Authentication" : "Recommended Security Setup"}
            </div>
            <h1 className="text-2xl font-semibold tracking-tight">
              {view === "manage" ? "Backup Recovery Codes" :
               view === "show-codes" ? "Save Your Recovery Codes" :
               "Configure Two-Factor Authentication"}
            </h1>
          </div>
        </div>

        {view === "loading" && (
          <div className="py-12 text-center text-sm text-brand-muted" data-testid="2fa-loading">Loading…</div>
        )}

        {view === "show-codes" && (
          <RecoveryCodesPanel
            codes={recoveryCodes}
            userEmail={userEmail}
            onAcknowledge={recoveryStatus ? finishRegen : finishEnrollment}
            acknowledgeLabel={recoveryStatus ? "Done — return to dashboard" : "I've saved my codes — sign in again"}
          />
        )}

        {view === "manage" && (
          <div className="space-y-5 text-sm" data-testid="2fa-manage-view">
            <div className="border border-emerald-200 bg-emerald-50 rounded-lg p-4 flex items-start gap-3" data-testid="2fa-enabled-status">
              <CheckCircle2 className="w-5 h-5 text-emerald-700 mt-0.5 shrink-0" />
              <div>
                <div className="font-medium text-emerald-900">Two-factor authentication is enabled.</div>
                <div className="text-xs text-emerald-800 mt-1">Your account is protected. You need your authenticator app to sign in.</div>
              </div>
            </div>

            <div className="border border-brand rounded-lg p-5">
              <div className="flex items-center gap-2 mb-3">
                <KeyRound className="w-5 h-5 text-brand-primary" />
                <div className="font-medium">Recovery codes</div>
              </div>
              {recoveryStatus && (
                <div className="text-sm text-brand-muted mb-3" data-testid="recovery-codes-status">
                  <b className="text-brand-text">{recoveryStatus.remaining} of {recoveryStatus.total}</b> codes remaining.
                  {recoveryStatus.generated_at && (
                    <span className="block text-xs mt-1">
                      Last generated {new Date(recoveryStatus.generated_at).toLocaleString()}.
                    </span>
                  )}
                  {recoveryStatus.remaining <= 2 && recoveryStatus.total > 0 && (
                    <div className="mt-2 p-2 rounded bg-amber-50 border border-amber-200 text-amber-900 text-xs flex items-start gap-1.5">
                      <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                      <span>You&apos;re running low on recovery codes. Regenerate a fresh set now to stay protected.</span>
                    </div>
                  )}
                </div>
              )}

              {!confirmRegen ? (
                <button
                  type="button"
                  onClick={() => setConfirmRegen(true)}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-md border border-brand bg-brand-bg text-brand-text text-sm font-medium hover:bg-brand-bg/70"
                  data-testid="recovery-codes-regen-button"
                >
                  <RefreshCw className="w-4 h-4" />
                  {recoveryStatus?.total ? "Regenerate recovery codes" : "Generate recovery codes"}
                </button>
              ) : (
                <div className="rounded-md border border-amber-300 bg-amber-50 p-3" data-testid="recovery-codes-regen-confirm">
                  <div className="flex items-start gap-2 mb-2">
                    <AlertTriangle className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
                    <div className="text-xs text-amber-900 leading-relaxed">
                      <b>This will invalidate ALL of your existing recovery codes</b>, including any you have not used yet. You will need to download and save the new set.
                    </div>
                  </div>
                  <div className="flex gap-2 mt-2">
                    <button
                      type="button"
                      onClick={regenerate}
                      disabled={regenerating}
                      className="flex-1 px-3 py-2 rounded-md bg-amber-700 text-white text-xs font-semibold hover:bg-amber-800 disabled:opacity-50"
                      data-testid="recovery-codes-regen-confirm-button"
                    >
                      {regenerating ? "Generating…" : "Yes — generate new codes"}
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmRegen(false)}
                      className="px-3 py-2 rounded-md border border-brand text-xs"
                      data-testid="recovery-codes-regen-cancel"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="text-center">
              <button
                type="button"
                onClick={() => navigate("/dashboard")}
                className="text-xs text-brand-muted underline hover:text-brand-text"
                data-testid="2fa-manage-back-to-dashboard"
              >
                Back to dashboard
              </button>
            </div>
          </div>
        )}

        {view === "enroll" && (
          <div className="space-y-5 text-sm">
            <div className="flex items-start gap-3 p-3 bg-brand-bg rounded-md">
              <Smartphone className="w-5 h-5 text-brand-primary mt-0.5" />
              <div>
                <div className="font-medium text-brand-text">1. Install an authenticator app</div>
                <div className="text-xs text-brand-muted mt-1">Google Authenticator, Authy, 1Password, or Microsoft Authenticator.</div>
              </div>
            </div>

            {setup && (
              <div className="border border-brand rounded-lg p-5">
                <div className="font-medium mb-3">2. Scan the QR code</div>
                <div className="flex flex-col sm:flex-row gap-5 items-center">
                  <img src={setup.qr} alt="2FA QR" className="w-44 h-44 rounded-md border border-brand" data-testid="totp-qr" />
                  <div className="flex-1 w-full">
                    <div className="text-xs text-brand-muted mb-1.5">Or enter this key manually (no spaces):</div>
                    <div className="flex items-center gap-2 p-2 bg-brand-bg rounded-md font-mono text-sm break-all select-all">
                      <span className="flex-1 tracking-wider" data-testid="totp-secret-display">
                        {setup.secret.match(/.{1,4}/g)?.join(" ") || setup.secret}
                      </span>
                      <button onClick={copy} className="p-1.5 hover:bg-white rounded" data-testid="copy-secret">
                        {copied ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                      </button>
                    </div>
                    <div className="mt-2 text-[10px] text-brand-muted leading-relaxed">
                      Authenticator apps strip spaces automatically. If manual entry fails, use the QR code instead. Make sure your phone&apos;s clock is set to automatic time.
                    </div>
                    <button
                      type="button"
                      onClick={regenerateQr}
                      disabled={regeneratingQr}
                      className="mt-3 inline-flex items-center gap-1.5 text-[11px] text-brand-primary hover:underline disabled:opacity-60"
                      data-testid="regenerate-qr"
                    >
                      <RefreshCw className={`w-3 h-3 ${regeneratingQr ? "animate-spin" : ""}`} />
                      Codes not working? Generate a new QR
                    </button>
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={verify} className="border border-brand rounded-lg p-5 space-y-3">
              <div className="font-medium">3. Enter the 6-digit code from your app</div>
              {clockDrift !== null && Math.abs(clockDrift) > 30 && (
                <div className="flex items-start gap-2 p-2.5 rounded-md bg-red-50 border border-red-200 text-[11px] text-red-800" data-testid="clock-drift-warning">
                  <Clock className="w-4 h-4 mt-0.5 shrink-0" />
                  <div>
                    <b>Your device clock is {Math.abs(clockDrift)}s {clockDrift > 0 ? "ahead of" : "behind"} the server.</b> Authenticator codes will fail. On your phone, enable <b>Set time automatically</b> (Android: Settings → System → Date & time; iOS: Settings → General → Date & Time).
                  </div>
                </div>
              )}
              {clockDrift !== null && Math.abs(clockDrift) <= 30 && (
                <div className="flex items-center gap-2 text-[11px] text-emerald-700" data-testid="clock-drift-ok">
                  <Clock className="w-3.5 h-3.5" />
                  <span>Device clock in sync with server ({clockDrift >= 0 ? "+" : ""}{clockDrift}s).</span>
                </div>
              )}
              <input value={code} onChange={(e)=>setCode(e.target.value.replace(/\D/g, ""))} required maxLength={6}
                className="w-full px-3 py-2.5 bg-white border border-brand rounded-md text-lg font-mono tracking-widest text-center"
                data-testid="2fa-verify-code" placeholder="000000" />
              <button type="submit" disabled={loading || code.length !== 6}
                className="w-full bg-brand-primary text-white py-2.5 rounded-md text-sm font-medium hover:bg-[#35096B] disabled:opacity-50"
                data-testid="2fa-verify-submit">
                {loading ? "Verifying…" : "Verify & Enable 2FA"}
              </button>
            </form>

            <div className="text-[11px] text-brand-muted text-center">
              After enabling, you&apos;ll receive 10 backup recovery codes. Save them — they let you sign in if you lose your phone.
            </div>

            {/* SKIP FOR NOW — hidden for admin role (HIPAA mandatory 2FA lockdown) */}
            {!isAdmin && (
              <div className="border-t border-brand pt-4 text-center" data-testid="2fa-skip-section">
                {!confirmSkip ? (
                  <button
                    type="button"
                    onClick={() => setConfirmSkip(true)}
                    className="text-xs text-brand-muted underline hover:text-brand-text"
                    data-testid="2fa-skip-link">
                    Skip for now (not recommended)
                  </button>
                ) : (
                  <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-left" data-testid="2fa-skip-warning">
                    <div className="flex items-start gap-2 mb-2">
                      <AlertTriangle className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
                      <div className="text-xs text-amber-900 leading-relaxed">
                        <b>Your account security may be compromised.</b> Two-factor authentication is strongly recommended. You can enable it anytime under Account Settings.
                      </div>
                    </div>
                    <div className="flex gap-2 mt-2">
                      <button
                        type="button"
                        onClick={skip}
                        disabled={skipping}
                        className="flex-1 px-3 py-2 rounded-md bg-amber-600 text-white text-xs font-semibold hover:bg-amber-700 disabled:opacity-50"
                        style={{ minHeight: 40 }}
                        data-testid="2fa-skip-confirm">
                        {skipping ? "Skipping…" : "I understand — continue without 2FA"}
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmSkip(false)}
                        className="px-3 py-2 rounded-md border border-brand text-xs"
                        style={{ minHeight: 40 }}
                        data-testid="2fa-skip-cancel">
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
            {isAdmin && (
              <div className="border-t border-brand pt-4 text-center text-[11px] text-red-700 bg-red-50 border border-red-200 rounded-md p-3" data-testid="2fa-admin-lockdown">
                <b>HIPAA lockdown:</b> Two-factor authentication is required for administrator accounts and cannot be skipped.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
